import { AnalysisAPIError } from "./analysis-jobs.ts";

export const WEB_ANALYTICS_SCHEMA_VERSION = 3;

const MAX_ANALYTICS_DURATION_MS = 60 * 60 * 1_000;

const AUTHENTICATION_FAILURE_KEYS = new Set([
  "account_data_deletion_pending",
  "account_deleted",
  "authentication_required",
  "firebase_identity_deletion_pending",
  "invalid_token",
  "token_expired",
  "unauthorized",
]);

const QUOTA_FAILURE_KEYS = new Set([
  "normal_backtest_quota_exceeded",
  "period_limit_exceeded",
]);

const RATE_LIMIT_FAILURE_KEYS = new Set([
  "backtest_request_limit_exceeded",
  "rate_limit_exceeded",
  "strategy_builder_request_limit_exceeded",
]);

const VALIDATION_FAILURE_KEYS = new Set([
  "custom_strategy_not_executable",
  "invalid_backtest_request",
  "invalid_period",
  "invalid_strategy_dsl",
  "strategy_builder_dsl_required",
]);

const MARKET_DATA_FAILURE_KEYS = new Set([
  "market_data_unavailable",
]);

const BACKEND_FAILURE_KEYS = new Set([
  "strategy_builder_unavailable",
]);

export type BacktestErrorCategory =
  | "authentication"
  | "backend"
  | "cancelled"
  | "market_data"
  | "network"
  | "quota"
  | "rate_limit"
  | "request"
  | "timeout"
  | "unknown"
  | "validation";

export type BacktestFailureStage =
  | "authentication"
  | "backend_processing"
  | "market_data"
  | "quota_check"
  | "request_validation"
  | "transport"
  | "unknown";

export type HTTPStatusBucket =
  | "no_response"
  | "1xx"
  | "2xx"
  | "3xx"
  | "4xx"
  | "5xx"
  | "other";

export type BacktestDurationBucket =
  | "under_1s"
  | "1_3s"
  | "3_10s"
  | "10_30s"
  | "30_60s"
  | "1_5m"
  | "5m_plus";

export interface BacktestDurationAnalyticsParameters {
  duration_ms: number;
  duration_bucket: BacktestDurationBucket;
}

export interface BacktestFailureAnalyticsParameters extends BacktestDurationAnalyticsParameters {
  error_category: BacktestErrorCategory;
  failure_stage: BacktestFailureStage;
  status_bucket: HTTPStatusBucket;
  retryable?: boolean;
}

function normalizeContractKey(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || undefined;
}

function stableContractKeys(error: AnalysisAPIError): string[] {
  return [error.reason, error.code, error.localizationKey]
    .map(normalizeContractKey)
    .filter((value): value is string => Boolean(value));
}

function hasAllowlistedKey(keys: string[], allowlist: Set<string>): boolean {
  return keys.some((key) => allowlist.has(key));
}

function statusForError(error: unknown): number | undefined {
  if (!(error instanceof AnalysisAPIError)) return undefined;
  return Number.isInteger(error.status) && error.status >= 100 && error.status <= 599
    ? error.status
    : undefined;
}

function statusBucket(status: number | undefined): HTTPStatusBucket {
  if (status === undefined) return "no_response";
  if (status < 100 || status > 599) return "other";
  return `${Math.floor(status / 100)}xx` as HTTPStatusBucket;
}

function errorName(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const name = (error as { name?: unknown }).name;
  return typeof name === "string" ? name : undefined;
}

function failureCategory(error: unknown, status: number | undefined): BacktestErrorCategory {
  if (error instanceof AnalysisAPIError) {
    const keys = stableContractKeys(error);
    if (hasAllowlistedKey(keys, AUTHENTICATION_FAILURE_KEYS)) return "authentication";
    if (hasAllowlistedKey(keys, QUOTA_FAILURE_KEYS)) return "quota";
    if (hasAllowlistedKey(keys, RATE_LIMIT_FAILURE_KEYS)) return "rate_limit";
    if (hasAllowlistedKey(keys, VALIDATION_FAILURE_KEYS)) return "validation";
    if (hasAllowlistedKey(keys, MARKET_DATA_FAILURE_KEYS)) return "market_data";
    if (hasAllowlistedKey(keys, BACKEND_FAILURE_KEYS)) return "backend";
  }

  if (status === 401 || status === 403) return "authentication";
  if (status === 408 || status === 504) return "timeout";
  if (status === 429) return "rate_limit";
  if (status !== undefined && status >= 500) return "backend";
  if (status !== undefined && status >= 400) {
    return [400, 409, 422].includes(status) ? "validation" : "request";
  }

  const name = errorName(error);
  if (name === "AbortError") return "cancelled";
  if (name === "NetworkError" || error instanceof TypeError) return "network";
  return "unknown";
}

function stageForCategory(category: BacktestErrorCategory): BacktestFailureStage {
  switch (category) {
    case "authentication":
      return "authentication";
    case "quota":
    case "rate_limit":
      return "quota_check";
    case "validation":
    case "request":
      return "request_validation";
    case "market_data":
      return "market_data";
    case "backend":
      return "backend_processing";
    case "cancelled":
    case "network":
    case "timeout":
      return "transport";
    default:
      return "unknown";
  }
}

function retryability(
  error: unknown,
  status: number | undefined,
  category: BacktestErrorCategory,
): boolean | undefined {
  if (error instanceof AnalysisAPIError && typeof error.retryable === "boolean") {
    return error.retryable;
  }
  if (["cancelled", "authentication", "quota", "request", "validation"].includes(category)) {
    return false;
  }
  if (["backend", "market_data", "network", "rate_limit", "timeout"].includes(category)) {
    return true;
  }
  if (status !== undefined && status >= 400 && status < 500) return false;
  return undefined;
}

export function backtestDurationAnalyticsParameters(
  startedAtMs: number,
  finishedAtMs: number,
): BacktestDurationAnalyticsParameters {
  const elapsed = finishedAtMs - startedAtMs;
  const durationMs = Math.min(
    MAX_ANALYTICS_DURATION_MS,
    Math.max(0, Number.isFinite(elapsed) ? Math.round(elapsed) : 0),
  );
  let durationBucket: BacktestDurationBucket = "5m_plus";
  if (durationMs < 1_000) durationBucket = "under_1s";
  else if (durationMs < 3_000) durationBucket = "1_3s";
  else if (durationMs < 10_000) durationBucket = "3_10s";
  else if (durationMs < 30_000) durationBucket = "10_30s";
  else if (durationMs < 60_000) durationBucket = "30_60s";
  else if (durationMs < 5 * 60_000) durationBucket = "1_5m";
  return { duration_ms: durationMs, duration_bucket: durationBucket };
}

/**
 * Converts an arbitrary backtest exception into finite analytics dimensions.
 * Raw messages, backend reason strings, prompts, and identifiers are never
 * copied into the returned parameters.
 */
export function backtestFailureAnalyticsParameters(
  error: unknown,
  startedAtMs: number,
  finishedAtMs: number,
): BacktestFailureAnalyticsParameters {
  const status = statusForError(error);
  const category = failureCategory(error, status);
  const retryable = retryability(error, status, category);
  return {
    ...backtestDurationAnalyticsParameters(startedAtMs, finishedAtMs),
    error_category: category,
    failure_stage: stageForCategory(category),
    status_bucket: statusBucket(status),
    ...(retryable === undefined ? {} : { retryable }),
  };
}
